// glonet-react-tailwind-project--src--components--GlonetAiLandingClassic.js
// FROZEN 2026-08-11: the original glonet.ai landing (React port of
// src/glonetai/Glonet.ai.dc.html), preserved as the admin-selectable
// alternative to the simplified GlonetAiLandingV2. Selected by the
// wc_settings.landing_variant switch (GTM tab → Settings → Landing page)
// or the ?landing=classic URL override — see GlonetAiLanding.js (selector).
// Do not evolve this file; new landing work happens in GlonetAiLandingV2.js.
// Post-freeze changes (kept to platform switches only):
//  - "Listen to the pre-meet" and other agent copy gated behind convOn
//    (the agent-conversation switch), like the rest of the agent copy.
//  - footer "The app" link behind show_app_link (admin opt-in, default
//    hidden), same as v2.
import React, { useEffect, useState } from "react";
import watercoolerLive from "../assets/glonetai/watercooler-live.png";
import { useNavigate, useLocation } from "react-router-dom";
import { Auth } from "aws-amplify";
import api from "../services/api";
import { API_PATHS } from "../config/apiConfig";
import { GLONET_AI_EVENT_ID } from "../config/glonetai";
import VoiceOnboarding from "./VoiceOnboarding";
import log from "../utils/logger";

const ACCENT = "#74a0f5";
const ACCENT_STRONG = "#5b8cf0";
const GLOW = "rgba(116,160,245,0.22)";
const LIVE_DOT = "#57d9a4"; // the cooler is always open

const MONO = "'JetBrains Mono',monospace";
const SERIF = "'Instrument Serif',serif";

function WaveBars({ playing }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "3px", height: "30px", flex: 1 }}>
      {Array.from({ length: 40 }).map((_, i) => {
        const h = 5 + Math.abs(Math.sin(i * 0.6 + 1)) * 22;
        return (
          <span
            key={i}
            style={{
              flex: 1,
              minWidth: "2px",
              height: h + "px",
              borderRadius: "2px",
              background: playing ? ACCENT : "rgba(255,255,255,0.15)",
              transformOrigin: "center",
              animation: playing ? `ga-wave 0.85s ease-in-out ${(i % 7) * 0.07}s infinite` : "none",
            }}
          />
        );
      })}
    </div>
  );
}

const MicIcon = ({ size = 17, stroke = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.7" strokeLinecap="round">
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11 a7 7 0 0 0 14 0" />
    <line x1="12" y1="18" x2="12" y2="21" />
  </svg>
);

const DoorIcon = ({ size = 16, stroke = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.7">
    <rect x="6" y="3" width="12" height="18" rx="2" />
    <circle cx="14.5" cy="12" r="1" fill={stroke} stroke="none" />
  </svg>
);

export default function GlonetAiLandingClassic() {
  const navigate = useNavigate();
  const location = useLocation();
  const [voiceOn, setVoiceOn] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [email, setEmail] = useState("");
  const [isAuthed, setIsAuthed] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [matchingInfo, setMatchingInfo] = useState(null); // {has_profile, is_attendee, invitation_code,...}
  const [accessNote, setAccessNote] = useState("");
  const [authEmail, setAuthEmail] = useState(""); // who's signed in (chip in the nav; empty = show nothing)
  const [isAdmin, setIsAdmin] = useState(false);  // admins get an Admin pill where "Join the nexus" was
  const [convOn, setConvOn] = useState(false);    // agent-conversation pipeline switch (pitch copy honesty)
  const [micSecs, setMicSecs] = useState(5);      // silent-mic prompt threshold (admin-tunable)
  const [appLinkOn, setAppLinkOn] = useState(false); // footer "The app" link — admin opt-in, hidden by default

  // Where this landing lives right now: "/" on the glonet.ai host, "/glonet-ai"
  // elsewhere. Used as the return target for sign-in/watercooler round-trips.
  const landingPath = location.pathname === "/" ? "/" : "/glonet-ai";
  const voiceReturnPath = landingPath === "/" ? "/?voice=1" : "/glonet-ai?voice=1";

  // Auth check + "who's around" signal; ?voice=1 reopens the widget after sign-in return.
  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(async (u) => {
        setIsAuthed(true);
        setAuthEmail((u && u.attributes && u.attributes.email) || "");
        api.get(API_PATHS.USERS.PROFILE)
          .then(({ data }) => setIsAdmin(!!data.is_admin))
          .catch(() => {});
        try {
          const { data } = await api.get(
            API_PATHS.EVENTS.MY_MATCHING_INFO.replace("{eventId}", GLONET_AI_EVENT_ID));
          setMatchingInfo(data);
        } catch (err) {
          log.error("my-matching-info fetch failed", { error: err });
        }
        if (new URLSearchParams(location.search).get("voice") === "1") setVoiceOn(true);
      })
      .catch(() => setIsAuthed(false));
    api.get(API_PATHS.PUBLIC.WATERCOOLER_ACTIVITY)
      .then(({ data }) => {
        setActiveCount(data.active_count || 0);
        setConvOn(!!data.conversations_enabled);   // pitch copy tracks the real switch
        if (data.mic_silence_secs) setMicSecs(data.mic_silence_secs);
        setAppLinkOn(!!data.show_app_link);
      })
      .catch(() => {});
  }, [location.search]);

  // Honest gate-based label: entries in the last ~10 min, not "online now".
  const liveLabel = activeCount > 0
    ? `${activeCount} HERE IN THE LAST 10 MIN`
    : "ALWAYS OPEN";

  const hasProfile = !!(matchingInfo && matchingInfo.has_profile);

  // Voice: sign-in first; then route non-attendees hitting a PRICED tier through
  // the normal Stripe flow before voice; otherwise open the live session inline.
  const startVoice = async () => {
    if (!isAuthed) {
      navigate("/signin", { state: { from: voiceReturnPath } });
      return;
    }
    if (matchingInfo && !matchingInfo.is_attendee) {
      try {
        const { data } = await api.get(
          API_PATHS.EVENTS.TIER_STATUS.replace("{eventId}", GLONET_AI_EVENT_ID));
        const price = data.available_tier ? parseFloat(data.available_tier.price) : 0;
        if (price > 0) {
          // Gate: subscription-mode tiers go to the new subscribe flow,
          // one_time tiers keep the original pricing flow.
          const paidPath = data.available_tier?.pricing_mode === "subscription"
            ? "subscribe" : "pricing";
          navigate(`/events/${GLONET_AI_EVENT_ID}/${paidPath}`, {
            state: {
              invitationCode: matchingInfo.invitation_code,
              eventId: GLONET_AI_EVENT_ID,
              // after Stripe, come back here and reopen the voice intake
              returnTo: voiceReturnPath,
            },
          });
          return;
        }
      } catch (err) {
        log.error("tier-status check failed; proceeding as free", { error: err });
      }
    }
    setVoiceOn((v) => !v);
  };

  const enterWaterCooler = (e) => {
    e.preventDefault();
    navigate("/watercooler", { state: { from: landingPath } });
  };

  const goHosts = (e) => {
    e.preventDefault();
    navigate("/hosts");
  };

  // "Request access": signed-in users already have access — open the voice
  // intake instead of a signup form. Signed-out users go to signup with the
  // email prefilled AND a return path so the funnel round-trips.
  // Sign out without leaving the landing page — the nav chip is the only
  // signed-in indicator here, so clear every trace of the session's state.
  const signOutHere = async () => {
    try { await Auth.signOut(); } catch (err) { /* already signed out */ }
    setIsAuthed(false);
    setAuthEmail("");
    setMatchingInfo(null);
    setVoiceOn(false);
    setAccessNote("");
  };

  const requestAccess = (e) => {
    e.preventDefault();
    if (isAuthed) {
      setAccessNote(`You're already signed in${authEmail ? ` as ${authEmail}` : ""} — no request needed. Opening the voice intake…`);
      if (!voiceOn) startVoice();
      const el = document.getElementById("voice-demo");
      if (el) el.scrollIntoView({ behavior: "smooth" });
      return;
    }
    navigate("/signup", { state: { email, from: voiceReturnPath } });
  };

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      style={{
        background: "#0c0d10",
        color: "#f1f2f4",
        minHeight: "100vh",
        fontFamily: "'Hanken Grotesk',system-ui,sans-serif",
        WebkitFontSmoothing: "antialiased",
        overflowX: "hidden",
        position: "relative",
      }}
    >
      <style>{`
        @keyframes ga-wave { 0%,100% { transform: scaleY(0.35); } 50% { transform: scaleY(1); } }
        @keyframes ga-pulse { 0% { box-shadow: 0 0 0 0 rgba(87,217,164,0.5); } 70% { box-shadow: 0 0 0 7px rgba(87,217,164,0); } 100% { box-shadow: 0 0 0 0 rgba(87,217,164,0); } }
        @keyframes ga-breathe { 0%,100% { opacity: 0.55; transform: translateX(-50%) scale(1); } 50% { opacity: 0.8; transform: translateX(-50%) scale(1.06); } }
        @keyframes ga-floaty { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .ga-link { color:#b6b9c0; text-decoration:none; transition:color .15s; }
        .ga-link:hover { color:#fff; }
        .ga-btn { transition:background .15s, border-color .15s; }
        .ga-btn-primary:hover { background:${ACCENT_STRONG} !important; }
        .ga-btn-ghost:hover { border-color:${ACCENT} !important; }
        .ga-foot { color:#7c818b; text-decoration:none; transition:color .15s; }
        .ga-foot:hover { color:#f1f2f4; }
        .ga-input:focus { border-color:${ACCENT} !important; }
        @media (max-width: 840px) {
          .ga-grid-3, .ga-grid-2 { grid-template-columns:1fr !important; gap:40px !important; }
          .ga-nav-links a:not(.ga-nav-cta) { display:none; }
        }
      `}</style>

      {/* ambient hero glow */}
      <div
        style={{
          position: "absolute",
          top: "-220px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "1000px",
          height: "680px",
          background: `radial-gradient(circle at center, ${GLOW} 0%, transparent 68%)`,
          filter: "blur(14px)",
          pointerEvents: "none",
          zIndex: 0,
          animation: "ga-breathe 9s ease-in-out infinite",
        }}
      />

      {/* NAV */}
      <nav
        style={{
          position: "relative",
          zIndex: 3,
          maxWidth: "1140px",
          margin: "0 auto",
          padding: "26px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: "12px" }}>
          <span style={{ fontFamily: SERIF, fontSize: "25px", letterSpacing: "-0.01em" }}>
            glonet<span style={{ color: ACCENT }}>.ai</span>
          </span>
          <span style={{ fontFamily: MONO, fontSize: "10px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>
            Global&nbsp;Network
          </span>
        </div>
        <div className="ga-nav-links" style={{ display: "flex", alignItems: "center", gap: "30px" }}>
          <a href="#how" onClick={scrollTo("how")} className="ga-link" style={{ fontSize: "14px" }}>How it works</a>
          <a href="#watercooler" onClick={scrollTo("watercooler")} className="ga-link" style={{ fontSize: "14px" }}>Water Cooler</a>
          {convOn && (
            <a href="#agents" onClick={scrollTo("agents")} className="ga-link" style={{ fontSize: "14px" }}>The Agents</a>
          )}
          <a href="/hosts" onClick={goHosts} className="ga-link" style={{ fontSize: "14px" }}>For hosts</a>
          {/* members don't need a join CTA; admins get the Admin door instead */}
          {!isAuthed && (
            <a
              href="#join"
              onClick={scrollTo("join")}
              className="ga-btn ga-btn-primary ga-nav-cta"
              style={{ fontSize: "14px", fontWeight: 600, color: "#0c0d10", background: ACCENT, textDecoration: "none", padding: "10px 18px", borderRadius: "999px", whiteSpace: "nowrap" }}
            >
              Join the nexus
            </a>
          )}
          {isAuthed && isAdmin && (
            <a
              href="/admin"
              onClick={(e) => { e.preventDefault(); navigate("/admin"); }}
              className="ga-nav-cta"
              style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.1em", fontWeight: 700,
                       color: "#fbbf24", background: "transparent", border: "1px solid rgba(251,191,36,0.55)",
                       textDecoration: "none", padding: "9px 16px", borderRadius: "999px", whiteSpace: "nowrap" }}
            >
              ADMIN
            </a>
          )}
          {/* signed-in chip: span+button so the mobile `a:not(.ga-nav-cta)`
              hide rule doesn't swallow it — signed-out shows nothing at all */}
          {isAuthed && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "10px", whiteSpace: "nowrap" }}>
              <span style={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "0.06em", color: "#7c818b" }}>
                {authEmail || "signed in"}
              </span>
              <button
                onClick={signOutHere}
                style={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "0.08em", cursor: "pointer",
                         color: "#f1f2f4", background: "transparent", border: "1px solid rgba(255,255,255,0.22)",
                         padding: "6px 12px", borderRadius: "999px" }}
              >
                SIGN OUT
              </button>
            </span>
          )}
        </div>
      </nav>

      {/* HERO */}
      <section style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "88px 32px 96px", textAlign: "center" }}>
        <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: ACCENT, textTransform: "uppercase", marginBottom: "30px" }}>
          One global nexus
        </div>
        <h1 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(42px,6.4vw,82px)", lineHeight: 1.02, letterSpacing: "-0.015em", margin: "0 auto", maxWidth: "14ch", textWrap: "balance" }}>
          Everyone worth meeting is one introduction away.
        </h1>
        <p style={{ fontSize: "19px", lineHeight: 1.6, color: "#b6b9c0", maxWidth: "60ch", margin: "30px auto 0" }}>
          Glonet is a single global network. Say what you're looking for — out loud — and the matchmaker finds your people{convOn ? ", lets your agents meet first," : ""} and emails you only when it's worth your time.
        </p>
        <div style={{ display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap", marginTop: "40px" }}>
          <button
            onClick={startVoice}
            className="ga-btn ga-btn-primary"
            style={{ display: "inline-flex", alignItems: "center", gap: "10px", fontFamily: "inherit", fontSize: "15px", fontWeight: 600, color: "#0c0d10", background: ACCENT, border: "none", padding: "15px 24px", borderRadius: "999px", cursor: "pointer" }}
          >
            <MicIcon />
            {hasProfile ? "Update matching info" : "Start with voice"}
          </button>
          <a
            href="/watercooler"
            onClick={enterWaterCooler}
            className="ga-btn ga-btn-ghost"
            style={{ display: "inline-flex", alignItems: "center", gap: "10px", fontSize: "15px", fontWeight: 500, color: "#f1f2f4", background: "transparent", border: "1px solid rgba(255,255,255,0.18)", padding: "15px 22px", borderRadius: "999px", textDecoration: "none" }}
          >
            <DoorIcon />
            Enter the Water Cooler
          </a>
        </div>

        {/* voice demo strip */}
        <div id="voice-demo" style={{ maxWidth: "560px", margin: "48px auto 0", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "18px", padding: "22px 24px", textAlign: "left" }}>
          {!voiceOn ? (
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <button
                onClick={startVoice}
                className="ga-btn ga-btn-ghost"
                style={{ flex: "none", width: "46px", height: "46px", borderRadius: "50%", border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.04)", color: "#f1f2f4", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
              >
                <MicIcon size={18} />
              </button>
              <div>
                <div style={{ fontSize: "14px", color: "#f1f2f4", fontWeight: 500 }}>
                  {hasProfile ? "Tap to update your matching info." : "Tap to talk — no forms."}
                </div>
                <div style={{ fontSize: "13px", color: "#7c818b", marginTop: "2px" }}>
                  {hasProfile
                    ? "The agent can read back or show what you have, then update it."
                    : "The voice agent asks what you want and what you bring."}
                  {!isAuthed && " Sign in first so your answers reach the matchmaker."}
                </div>
              </div>
            </div>
          ) : (
            <VoiceOnboarding
              micSilenceSecs={micSecs}
              onEnd={() => {
                setVoiceOn(false);
                // refresh so the button flips to "Update matching info" after first save
                api.get(API_PATHS.EVENTS.MY_MATCHING_INFO.replace("{eventId}", GLONET_AI_EVENT_ID))
                  .then(({ data }) => setMatchingInfo(data))
                  .catch(() => {});
              }}
            />
          )}
        </div>
      </section>

      <div style={{ maxWidth: "1140px", margin: "0 auto", height: "1px", background: "rgba(255,255,255,0.07)" }} />

      {/* HOW IT WORKS */}
      <section id="how" style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "104px 32px" }}>
        <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>How it works</div>
        <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(30px,3.6vw,46px)", lineHeight: 1.08, letterSpacing: "-0.01em", margin: "18px 0 0", maxWidth: "18ch" }}>
          Three steps. Then you just show up.
        </h2>
        <div className="ga-grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "30px", marginTop: "56px" }}>
          {[
            ["01 / Speak", "Join by talking", "A voice agent asks what you want and what you offer — and writes it up for you. No forms, no profile to fill out."],
            // the pre-meeting pitch only when the conversation pipeline is actually on
            ["02 / Match", "The matchmaker scores the network",
             convOn
               ? "Glonet ranks the whole nexus for fit. Your agent and theirs hold a short pre-meeting — read the transcript free; hear the audio with premium."
               : "Glonet ranks the whole nexus for fit."],
            ["03 / Connect", "An intro lands in your inbox", "When there's a real reason, we email you both. Step into the Water Cooler and pick up the conversation live."],
          ].map(([step, title, body]) => (
            <div key={step}>
              <div style={{ fontFamily: MONO, fontSize: "13px", color: ACCENT, marginBottom: "14px" }}>{step}</div>
              <h3 style={{ fontSize: "21px", fontWeight: 600, margin: "0 0 10px" }}>{title}</h3>
              <p style={{ fontSize: "16px", lineHeight: 1.6, color: "#9a9ea7", margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      <div style={{ maxWidth: "1140px", margin: "0 auto", height: "1px", background: "rgba(255,255,255,0.07)" }} />

      {/* WATER COOLER */}
      <section id="watercooler" className="ga-grid-2" style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "104px 32px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "64px", alignItems: "center" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>The Water Cooler</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", fontFamily: MONO, fontSize: "11px", letterSpacing: "0.14em", color: LIVE_DOT, border: "1px solid rgba(255,255,255,0.12)", borderRadius: "999px", padding: "4px 10px" }}>
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: LIVE_DOT, animation: "ga-pulse 1.8s infinite" }} />
              {liveLabel}
            </span>
          </div>
          <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(30px,3.6vw,48px)", lineHeight: 1.06, letterSpacing: "-0.01em", margin: "18px 0 0", maxWidth: "14ch" }}>
            A room that's always open.
          </h2>
          <p style={{ fontSize: "18px", lineHeight: 1.65, color: "#b6b9c0", margin: "22px 0 0", maxWidth: "46ch" }}>
            One always-on space for the whole nexus — our own room, in the page. When a match steps in, you get a ping; walk over and the conversation just starts, voice and video, no links and no scheduling.
          </p>
          <a
            href="/watercooler"
            onClick={enterWaterCooler}
            className="ga-btn ga-btn-primary"
            style={{ display: "inline-flex", alignItems: "center", gap: "10px", marginTop: "30px", fontSize: "15px", fontWeight: 600, color: "#0c0d10", background: ACCENT, padding: "14px 22px", borderRadius: "999px", textDecoration: "none" }}
          >
            <DoorIcon />
            Enter the Water Cooler
          </a>
        </div>
        <div style={{ position: "relative" }}>
          <div style={{ aspectRatio: "4/3", borderRadius: "18px", border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "#121419", overflow: "hidden" }}>
            <img src={watercoolerLive} alt="The Water Cooler — the live space"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          </div>
          <div style={{ position: "absolute", right: "-14px", bottom: "-14px", background: "#16181d", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "14px", padding: "12px 16px", display: "flex", alignItems: "center", gap: "10px", animation: "ga-floaty 5s ease-in-out infinite" }}>
            <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: LIVE_DOT }} />
            <span style={{ fontSize: "13px", color: "#dfe1e5" }}>Marcus just stepped in</span>
          </div>
        </div>
      </section>

      <div style={{ maxWidth: "1140px", margin: "0 auto", height: "1px", background: "rgba(255,255,255,0.07)" }} />

      {/* AGENTS — whole section only exists while the conversation pipeline is
          on; premium framing: transcripts free, audio paid */}
      {convOn && (
      <section id="agents" className="ga-grid-2" style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "104px 32px", display: "grid", gridTemplateColumns: "1fr 1.15fr", gap: "64px", alignItems: "center" }}>
        <div>
          <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>
            Agent pre-screen
            <span style={{ marginLeft: "10px", fontSize: "10px", letterSpacing: "0.18em", color: "#0c0d10", background: ACCENT, padding: "3px 8px", borderRadius: "999px" }}>PREMIUM</span>
          </div>
          <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(30px,3.6vw,48px)", lineHeight: 1.06, letterSpacing: "-0.01em", margin: "18px 0 0", maxWidth: "14ch" }}>
            Your agents meet before you do.
          </h2>
          <p style={{ fontSize: "18px", lineHeight: 1.65, color: "#b6b9c0", margin: "22px 0 0", maxWidth: "44ch" }}>
            Each agent represents your objective — and respects your dealbreakers. They hold a short, honest pre-meeting so you can listen in and decide if it's worth a real conversation.
          </p>
          <p style={{ fontSize: "14px", lineHeight: 1.6, color: "#7c818b", margin: "14px 0 0", maxWidth: "44ch" }}>
            Every member reads the transcript. Premium members get the audio — with their best match, every week.
          </p>
        </div>
        {/* transcript card */}
        <div style={{ background: "#131519", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "20px", padding: "24px", boxShadow: "0 24px 60px -30px rgba(0,0,0,0.7)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: "16px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
            <span style={{ fontSize: "14px", fontWeight: 600 }}>Dana × Marcus — pre-meet</span>
            <span style={{ fontFamily: MONO, fontSize: "11px", color: "#7c818b" }}>94% fit</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "14px", padding: "18px 0" }}>
            {[
              ["DANA·AI", ACCENT, "She's raising a seed round for agent infrastructure and wants a lead who's comfortable pre-revenue."],
              ["MARCUS·AI", "#8a8f98", "That's squarely Marcus's thesis — he leads seed rounds in dev infrastructure and has sat on two agent-tooling boards."],
              ["DANA·AI", ACCENT, "One filter: she only wants investors who can open doors to design partners, not just capital."],
              ["MARCUS·AI", "#8a8f98", "He has 40+ engineering leaders piloting agent tools right now. Worth a real conversation."],
            ].map(([who, color, line], i) => (
              <div key={i} style={{ display: "flex", gap: "10px" }}>
                <span style={{ flex: "none", fontFamily: MONO, fontSize: "10px", letterSpacing: "0.08em", color, width: "58px", paddingTop: "2px" }}>{who}</span>
                <p style={{ margin: 0, fontSize: "14.5px", lineHeight: 1.55, color: "#cfd2d8" }}>{line}</p>
              </div>
            ))}
          </div>
          {/* audio player */}
          <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 16px", background: "rgba(255,255,255,0.03)", borderRadius: "12px" }}>
            <button
              onClick={() => setAudioPlaying((p) => !p)}
              className="ga-btn ga-btn-primary"
              style={{ flex: "none", width: "38px", height: "38px", borderRadius: "50%", background: ACCENT, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              {audioPlaying ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#0c0d10">
                  <rect x="6" y="5" width="4" height="14" rx="1" />
                  <rect x="14" y="5" width="4" height="14" rx="1" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#0c0d10">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
            <WaveBars playing={audioPlaying} />
            <span style={{ flex: "none", fontFamily: MONO, fontSize: "11px", color: "#7c818b" }}>1:48</span>
          </div>
          <div style={{ marginTop: "16px", fontSize: "13.5px", lineHeight: 1.5, color: "#9a9ea7" }}>
            <span style={{ color: ACCENT, fontWeight: 600 }}>Recommendation:</span> strong fit — worth a live intro in the Water Cooler.
          </div>
        </div>
      </section>
      )}

      <div style={{ maxWidth: "1140px", margin: "0 auto", height: "1px", background: "rgba(255,255,255,0.07)" }} />

      {/* MATCHMAKER EMAIL */}
      <section className="ga-grid-2" style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "104px 32px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "64px", alignItems: "center" }}>
        <div>
          <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>The matchmaker</div>
          <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(30px,3.6vw,48px)", lineHeight: 1.06, letterSpacing: "-0.01em", margin: "18px 0 0", maxWidth: "14ch" }}>
            A chief of staff for your network.
          </h2>
          <p style={{ fontSize: "18px", lineHeight: 1.65, color: "#b6b9c0", margin: "22px 0 0", maxWidth: "44ch" }}>
            No dashboard to check. The matchmaker watches the nexus and emails you when someone's worth meeting — with the why{convOn ? ", the agent transcript," : ""} and a door straight into the Water Cooler.
          </p>
        </div>
        {/* email card */}
        <div style={{ background: "#15171c", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "18px", overflow: "hidden", boxShadow: "0 24px 60px -30px rgba(0,0,0,0.7)" }}>
          <div style={{ padding: "18px 22px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
              <span style={{ width: "26px", height: "26px", borderRadius: "50%", background: ACCENT, color: "#0c0d10", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: SERIF, fontSize: "15px" }}>g</span>
              <span style={{ fontSize: "13px", color: "#b6b9c0" }}>Glonet Matchmaker</span>
              <span style={{ fontFamily: MONO, fontSize: "11px", color: "#5c616b" }}>matchmaker@glonet.ai</span>
            </div>
            <div style={{ fontSize: "18px", fontWeight: 600 }}>You should meet Marcus.</div>
          </div>
          <div style={{ padding: "20px 22px" }}>
            <p style={{ margin: 0, fontSize: "15px", lineHeight: 1.6, color: "#cfd2d8" }}>
              You're raising for dev-tooling infra. Marcus leads seed rounds in exactly this space and can open doors to design partners.{convOn ? " Your agents already talked — here's the two-minute version." : ""}
            </p>
            <div style={{ display: "flex", gap: "10px", marginTop: "20px", flexWrap: "wrap" }}>
              {/* pre-meet audio only exists while the agent-conversation pipeline is on */}
              {convOn && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13.5px", fontWeight: 600, color: "#f1f2f4", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "999px", padding: "10px 16px" }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                Listen to the pre-meet
              </span>
              )}
              <a href="/watercooler" onClick={enterWaterCooler}
                 style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13.5px", fontWeight: 600, color: "#0c0d10", background: ACCENT, borderRadius: "999px", padding: "10px 16px", textDecoration: "none", cursor: "pointer" }}>
                Enter the Water Cooler →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* JOIN — the whole pitch + request form is for visitors; members see
          only the platform tagline */}
      <section id="join" style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: isAuthed ? "56px 32px" : "120px 32px 110px", textAlign: "center" }}>
        {!isAuthed && (
          <>
            <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(40px,5.6vw,72px)", lineHeight: 1.02, letterSpacing: "-0.015em", margin: 0 }}>Join the network.</h2>
            <p style={{ fontSize: "18px", lineHeight: 1.6, color: "#b6b9c0", maxWidth: "48ch", margin: "22px auto 0" }}>
              One nexus. The right introductions. Start by telling us what you're looking for.
            </p>
            <form onSubmit={requestAccess} style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap", margin: "38px auto 0", maxWidth: "480px" }}>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="ga-input"
                style={{ flex: 1, minWidth: "240px", fontFamily: "inherit", fontSize: "15px", color: "#f1f2f4", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "999px", padding: "15px 22px", outline: "none" }}
              />
              <button
                type="submit"
                className="ga-btn ga-btn-primary"
                style={{ fontFamily: "inherit", fontSize: "15px", fontWeight: 600, color: "#0c0d10", background: ACCENT, border: "none", padding: "15px 26px", borderRadius: "999px", cursor: "pointer" }}
              >
                Request access
              </button>
            </form>
            {accessNote && (
              <div style={{ marginTop: "16px", fontSize: "14px", color: LIVE_DOT }}>{accessNote}</div>
            )}
          </>
        )}
        <div style={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "0.1em", color: "#5c616b", marginTop: isAuthed ? 0 : "20px" }}>
          glonet.ai · powered by the Glonet platform
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ position: "relative", zIndex: 2, borderTop: "1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ maxWidth: "1140px", margin: "0 auto", padding: "30px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "20px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
            <span style={{ fontFamily: SERIF, fontSize: "20px" }}>glonet<span style={{ color: ACCENT }}>.ai</span></span>
            <span style={{ fontFamily: MONO, fontSize: "10px", letterSpacing: "0.24em", color: "#5c616b", textTransform: "uppercase" }}>Global Network</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "26px", fontSize: "13px", color: "#7c818b" }}>
            <a href="/hosts" onClick={goHosts} className="ga-foot">Run your own nexus</a>
            {appLinkOn && (
              <a href="https://mml-glonet.com" target="_blank" rel="noopener noreferrer" className="ga-foot">The app ↗</a>
            )}
            <a href="/watercooler" onClick={enterWaterCooler} className="ga-foot">Water Cooler</a>
            <span>© 2026</span>
          </div>
        </div>
      </footer>

      {/* FLOATING WATER COOLER DOOR */}
      <a
        href="/watercooler"
        onClick={enterWaterCooler}
        title="Water Cooler — drop in"
        className="ga-btn ga-btn-ghost"
        style={{ position: "fixed", right: "26px", bottom: "26px", zIndex: 50, display: "flex", alignItems: "center", gap: "12px", background: "#16181d", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "999px", padding: "11px 18px 11px 13px", textDecoration: "none", boxShadow: "0 16px 40px -16px rgba(0,0,0,0.8)" }}
      >
        <span style={{ position: "relative", flex: "none", width: "38px", height: "38px", borderRadius: "50%", background: "rgba(255,255,255,0.04)", display: "flex", alignItems: "center", justifyContent: "center", color: ACCENT }}>
          <DoorIcon size={18} />
          <span style={{ position: "absolute", top: "-1px", right: "-1px", width: "9px", height: "9px", borderRadius: "50%", background: LIVE_DOT, border: "2px solid #16181d", animation: "ga-pulse 1.8s infinite" }} />
        </span>
        <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.25, whiteSpace: "nowrap" }}>
          <span style={{ fontSize: "13px", fontWeight: 600, color: "#f1f2f4" }}>Water Cooler</span>
          <span style={{ fontFamily: MONO, fontSize: "9.5px", letterSpacing: "0.14em", color: LIVE_DOT }}>{liveLabel}</span>
        </span>
      </a>
    </div>
  );
}
